import React from "react";
import { Link } from "react-router-dom";

import SEO from "../../seo/SEO";

import {
  Brain,
  ArrowRight,
  CheckCircle2,
  Cpu,
  Database,
  ShieldCheck,
  Cloud,
  Bot,
  Sparkles,
  Workflow,
  LineChart,
  Network,
} from "lucide-react";

import "./AISoftwareSolutions.css";


/* =========================================================
   AI SERVICES
========================================================= */

const aiServices = [
  {
    icon: <Brain size={28} />,

    title: "AI-Powered Enterprise Solutions",

    description:
      "Custom AI software platforms designed for intelligent automation, predictive analytics, operational optimization and enterprise-scale transformation.",
  },

  {
    icon: <Bot size={28} />,

    title: "AI Agents & Conversational Systems",

    description:
      "Advanced AI assistants, autonomous AI agents, chatbot ecosystems and multilingual conversational intelligence platforms.",
  },

  {
    icon: <Workflow size={28} />,

    title: "Workflow Automation Systems",

    description:
      "AI-driven business process automation with intelligent workflows, decision systems and realtime operational orchestration.",
  },

  {
    icon: <Cpu size={28} />,

    title: "Machine Learning Infrastructure",

    description:
      "Scalable ML pipelines, model training infrastructure, inference systems and enterprise AI deployment architecture.",
  },

  {
    icon: <Database size={28} />,

    title: "AI Data Engineering",

    description:
      "Enterprise data lakes, vector databases, realtime data pipelines and intelligent knowledge retrieval systems.",
  },

  {
    icon: <Cloud size={28} />,

    title: "Cloud AI Architecture",

    description:
      "Cloud-native AI systems with scalable microservices, distributed compute infrastructure and high-availability backend environments.",
  },

  {
    icon: <Network size={28} />,

    title: "Generative AI Integration",

    description:
      "LLM-powered applications, RAG architecture, multimodal AI systems and enterprise-grade generative AI integrations.",
  },

  {
    icon: <LineChart size={28} />,

    title: "Predictive Analytics Platforms",

    description:
      "AI-powered forecasting systems, behavioral analytics, anomaly detection and realtime business intelligence solutions.",
  },
];


/* =========================================================
   TECHNOLOGY STACK
========================================================= */

const technologies = [
  "OpenAI",
  "LangChain",
  "LlamaIndex",
  "Python",
  "FastAPI",
  "TensorFlow",
  "PyTorch",
  "Vector Databases",
  "Redis",
  "PostgreSQL",
  "Docker",
  "Kubernetes",
  "AWS",
  "Azure AI",
  "NVIDIA CUDA",
  "React.js",
];


/* =========================================================
   AI SOFTWARE SOLUTIONS PAGE
========================================================= */

export default function AISoftwareSolutions() {

  /* =======================================================
     CANONICAL URL
  ======================================================= */

  const canonicalUrl =
    "https://www.bairagi-consultants.com/ai-software-solutions";


  /* =======================================================
     SERVICE STRUCTURED DATA
  ======================================================= */

  const serviceSchema = {
    "@context": "https://schema.org",

    "@type": "Service",

    name: "AI Software Development & Automation Services",

    serviceType:
      "Artificial Intelligence Software Development",

    url: canonicalUrl,

    description:
      "Custom AI software development including AI agents, generative AI applications, intelligent automation, machine learning systems, predictive analytics, document intelligence and enterprise AI integration.",

    provider: {
      "@type": "Organization",

      name: "Bairagi Consultants",

      url: "https://www.bairagi-consultants.com",
    },

    areaServed: {
      "@type": "Country",

      name: "India",
    },
  };


  return (
    <>

      {/* =====================================================
          SEO
      ====================================================== */}

      <SEO
        title="AI Software Development Company in India | Bairagi Consultants"
        description="Custom AI software development for AI agents, generative AI, intelligent automation, machine learning, predictive analytics and enterprise AI applications."
        path="/ai-software-solutions"
        schema={serviceSchema}
      />


      <div className="ai-page">

        {/* =================================================
            HERO
        ================================================= */}

        <section className="ai-hero ai-container">

          {/* LEFT */}

          <div className="ai-hero-content">

            <span className="ai-badge">
              Artificial Intelligence Solutions
            </span>


            <h1 className="ai-title">

              Intelligent

              <span>
                {" "}AI Software
              </span>

              <br />

              For Future-Ready Businesses

            </h1>


            <p className="ai-description">

              We design custom AI software systems
              powered by generative AI, machine learning,
              intelligent automation, predictive analytics
              and scalable cloud-ready architecture.

            </p>


            {/* ACTIONS */}

            <div className="ai-actions">

              <Link
                to="/contact?service=ai"
                className="ai-primary-btn"
              >

                Build AI Platform

                <ArrowRight size={18} />

              </Link>


              <Link
                to="/case-studies"
                className="ai-secondary-btn"
              >

                Explore AI Solutions

              </Link>

            </div>

          </div>


          {/* RIGHT */}

          <div className="ai-hero-card">

            <img
              src="https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=1400&auto=format&fit=crop"
              alt="AI software development and intelligent automation solutions"
            />

          </div>

        </section>


        {/* =================================================
            SERVICES
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-section-header">

            <h2>
              AI Software
              <br />
              Development Services
            </h2>


            <p>

              Custom artificial intelligence systems
              designed for automation, intelligent
              decision-making, predictive analytics,
              enterprise workflows and scalable
              digital transformation.

            </p>

          </div>


          <div className="ai-services-grid">

            {aiServices.map((service, index) => (

              <div
                key={index}
                className="ai-service-card"
              >

                {service.icon}

                <h3>
                  {service.title}
                </h3>

                <p>
                  {service.description}
                </p>

              </div>

            ))}

          </div>

        </section>


        {/* =================================================
            BUSINESS USE CASES
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-section-header">

            <h2>
              AI Solutions
              <br />
              For Business Automation
            </h2>


            <p>

              Artificial intelligence can be integrated
              into existing business applications to
              automate repetitive work, analyze data,
              assist employees and improve operational
              decision-making.

            </p>

          </div>


          <div className="ai-services-grid">

            <div className="ai-service-card">

              <Bot size={28} />

              <h3>
                AI Assistants & Copilots
              </h3>

              <p>
                Build intelligent assistants that
                answer questions, retrieve business
                information and assist customers
                or internal teams.
              </p>

            </div>


            <div className="ai-service-card">

              <Workflow size={28} />

              <h3>
                Business Process Automation
              </h3>

              <p>
                Automate document handling,
                enquiries, workflows, approvals
                and repetitive operational tasks
                using AI-assisted systems.
              </p>

            </div>


            <div className="ai-service-card">

              <LineChart size={28} />

              <h3>
                AI Analytics & Forecasting
              </h3>

              <p>
                Analyze operational and business
                data to identify patterns, anomalies,
                trends and forecasting opportunities.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            GENERATIVE AI
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-section-header">

            <h2>
              Generative AI
              <br />
              Application Development
            </h2>


            <p>

              We build generative AI applications
              using large language models, retrieval
              systems and business data to create
              intelligent software tailored to
              specific operational requirements.

            </p>

          </div>


          <div className="ai-services-grid">

            <div className="ai-service-card">

              <Sparkles size={28} />

              <h3>
                LLM Applications
              </h3>

              <p>
                Develop applications using large
                language models for intelligent
                search, summarization, assistance
                and business workflows.
              </p>

            </div>


            <div className="ai-service-card">

              <Database size={28} />

              <h3>
                RAG & Knowledge Systems
              </h3>

              <p>
                Connect AI applications with
                organizational documents, databases
                and knowledge repositories using
                retrieval-augmented generation.
              </p>

            </div>


            <div className="ai-service-card">

              <Network size={28} />

              <h3>
                AI Integration
              </h3>

              <p>
                Integrate generative AI capabilities
                with CRM, ERP, MIS, ecommerce,
                mobile applications and existing
                business software.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            TECH STACK
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-section-header">

            <h2>
              AI Development
              <br />
              Technology Stack
            </h2>


            <p>

              Modern AI frameworks, backend
              technologies, data infrastructure
              and cloud platforms for building
              scalable intelligent applications.

            </p>

          </div>


          <div className="ai-tech-stack">

            {technologies.map((tech) => (

              <span
                key={tech}
                className="ai-tech-tag"
              >

                {tech}

              </span>

            ))}

          </div>

        </section>


        {/* =================================================
            FEATURED SOLUTION
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-case-study">

            <span>
              Featured AI Solution
            </span>


            <h2>
              Enterprise Intelligence Platform
            </h2>


            <p>

              An enterprise AI platform can combine
              intelligent automation, AI copilots,
              predictive analytics, document
              intelligence, vector search and
              operational dashboards within a
              unified software ecosystem.

            </p>


            <Link
              to="/case-studies"
              className="ai-secondary-btn"
            >

              Explore Case Studies

              <ArrowRight size={18} />

            </Link>

          </div>

        </section>


        {/* =================================================
            WHY CHOOSE US
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-section-header">

            <h2>
              Why Choose Bairagi Consultants
              <br />
              for AI Software Development?
            </h2>

          </div>


          <div className="ai-services-grid">

            <div className="ai-service-card">

              <CheckCircle2 size={28} />

              <h3>
                Business-Focused AI
              </h3>

              <p>

                AI solutions are designed around
                real operational requirements,
                business workflows and measurable
                use cases.

              </p>

            </div>


            <div className="ai-service-card">

              <Sparkles size={28} />

              <h3>
                Generative AI Integration
              </h3>

              <p>

                Build LLM applications, RAG
                systems, AI assistants and
                intelligent business workflows
                using suitable AI technologies.

              </p>

            </div>


            <div className="ai-service-card">

              <ShieldCheck size={28} />

              <h3>
                Secure & Scalable Architecture
              </h3>

              <p>

                AI applications can be deployed
                through secure APIs, controlled
                access, cloud infrastructure and
                scalable backend systems.

              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            AI + BUSINESS SOFTWARE INTEGRATION
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-section-header">

            <h2>
              Integrate AI With
              <br />
              Your Business Software
            </h2>


            <p>

              AI does not always require replacing
              existing software. Intelligent features
              can be integrated with CRM, ERP, MIS,
              ecommerce, dashboards and mobile
              applications through secure APIs
              and backend services.

            </p>

          </div>


          <div className="ai-services-grid">

            <div className="ai-service-card">

              <Database size={28} />

              <h3>
                Document Intelligence
              </h3>

              <p>
                Extract, classify, search and
                process information from business
                documents using AI-assisted
                workflows.
              </p>

            </div>


            <div className="ai-service-card">

              <Cpu size={28} />

              <h3>
                Custom Machine Learning
              </h3>

              <p>
                Develop machine learning models
                and inference services for
                suitable prediction, classification
                and analytical requirements.
              </p>

            </div>


            <div className="ai-service-card">

              <Cloud size={28} />

              <h3>
                AI APIs & Cloud Deployment
              </h3>

              <p>
                Deploy AI functionality through
                scalable APIs and integrate it
                securely with web, mobile and
                enterprise applications.
              </p>

            </div>

          </div>

        </section>


        {/* =================================================
            FINAL CTA
        ================================================= */}

        <section className="ai-section ai-container">

          <div className="ai-cta">

            <h2>

              Ready To Build
              <br />

              Intelligent AI Solutions?

            </h2>


            <p>

              Let's discuss your AI use case,
              existing business process, available
              data and automation requirements to
              determine the right implementation
              approach.

            </p>


            <Link
              to="/contact?service=ai"
              className="ai-primary-btn"
            >

              Schedule AI Consultation

              <ArrowRight size={18} />

            </Link>

          </div>

        </section>

      </div>

    </>
  );
}